import React, { useRef, useState, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux' // https://github.com/gcoro/react-qrcode-logo
import QRCode from 'qrcode.react'
import { nanoid } from 'nanoid'
import classNames from 'classnames'

import TwoColumns, { Column, InnerBox, OuterBox } from './two-columns'
import styles from '../component-styles/create-qr-modal.module.scss'
import Button from './button'
import Input, { Label } from './input'
import Modal, { ModalHead, ModalBody, ModalFooter } from './modal'
import Heading from './heading'
import { downloadQRCode } from '../api-client/track-client'
import {
  deleteQrLogo,
  getQrLogoImage,
  uploadQrLogo,
} from '../api-client/graphql-client'
import ConfirmationButtons from './confirmation-buttons'
import { getItemByKeyValue, returnUnique } from '../helpers'
import { GraphQLState } from '../store/graphql-reducer'
import { getUserAccountDetailsGQL, logActionAsync } from '../store/actions'
import { presetColors } from '../core/variables'
import deafultLogoImage from '../assets/uplifter-arrow.png'
import { ImgUploader } from './img-uploader'

interface ClipboardItemData {
  [mimeType: string]: Blob | string | Promise<Blob | string>
}

interface ClipboardItem {
  readonly types: string[]
  readonly presentationStyle: 'unspecified' | 'inline' | 'attachment'
  getType(): Promise<Blob>
}

declare const ClipboardItem: {
  prototype: ClipboardItem
  new (itemData: ClipboardItemData): ClipboardItem
}

interface ColourPickerProps {
  color: string
  setColor: (val: string) => void
  defaultColour: string
}

export function ColourPicker({
  color,
  setColor,
  defaultColour,
}: ColourPickerProps): React.ReactElement {
  const [key, setKey] = useState(nanoid())
  const useColours = returnUnique([defaultColour, ...presetColors]).slice(0, 4)

  useEffect(() => {
    setKey(nanoid())
  }, [useColours.length])

  return (
    <div className={styles.swatchContainerWrapper}>
      <div className={styles.swatchContainer}>
        {useColours.map((presetColor) => (
          <Button
            key={presetColor}
            type="swatch"
            color={presetColor}
            onClick={() => setColor(presetColor)}
            style={{
              backgroundColor: presetColor,
              border: `${
                presetColor === '#FFFFFF' ? '1px solid black' : 'none'
              }`,
            }}
          />
        ))}
      </div>
      <div className={styles.colorField}>
        <Input
          key={key}
          name="color"
          id="color"
          value={color}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const { value: val } = event.target
            setColor(val)
          }}
        />
      </div>
    </div>
  )
}

const sizes = [
  {
    label: 'Small (600 x 600 px)',
    size: '600',
    value: '300',
    qrSize: 70,
  },
  {
    label: 'Medium (1000 x 1000 px)',
    size: '1000',
    value: '500',
    qrSize: 150,
  },
  {
    label: 'Large (2000 x 2000 px)',
    size: '2000',
    value: '1000',
    qrSize: 300,
  },
]

const setDefault = (accountData, key, defaultValue) => {
  if (
    accountData &&
    accountData.qrSettings &&
    accountData.qrSettings.length > 0
  ) {
    const _val = getItemByKeyValue(accountData.qrSettings, 'name', key)
    if (_val !== -1) {
      return _val.value
    }
  }
  return defaultValue
}

interface Props {
  onSuccess?: () => void
  setShowModal?: (status: boolean) => void
  codes?: any
  selectedCodes?: string[]
  useCode?: string
  section?: string
  returnQrCodeOnly?: boolean
  disableCopy?: boolean
  children?: any
}

export default function CreateQrModal({
  onSuccess,
  setShowModal,
  codes = [],
  selectedCodes = [],
  useCode = '',
  section = '',
  returnQrCodeOnly = false,
  disableCopy = false,
  children,
}: Props): React.ReactElement {
  const showShape = false
  const showTransparency = false

  const dispatch = useDispatch()
  const { graphql }: { graphql: GraphQLState } = useSelector((s: any): any => s)

  const { currentAccount } = graphql

  useEffect(() => {
    if (!currentAccount) {
      dispatch(getUserAccountDetailsGQL())
    }
  }, [currentAccount])

  // const dispatch = useDispatch()
  const [loaded, setLoaded] = useState(false)
  const [showLogo, setShowLogo] = useState(true)
  const [defaultShowUploader, setDefaultShowUploader] = useState(false)
  const [selectedValue, setSelectedValue] = useState('300')
  const [selectedType, setSelectedType] = useState('png')
  const [code, setCode] = useState(useCode)
  const defaultFgColour = '#000000'
  const [fgColour, setFgColour] = useState(defaultFgColour)
  const defaultBgColour = '#FFFFFF'
  const [bgColour, setBgColour] = useState(defaultBgColour)
  const defaultQrStyle = 'squares'
  const [qrStyle, setQrStyle] = useState<'dots' | 'squares'>(defaultQrStyle)
  const [imageFile, setImageFile] = useState('')
  const [qrTransparentLogo, setQrTransparentLogo] = useState(false)
  const previewRef = useRef(null)
  const ref = useRef(null)
  const canvas = useRef(null)

  useEffect(() => {
    const _fgColour = setDefault(currentAccount, 'fgColour', defaultFgColour)
    const _bgColour = setDefault(currentAccount, 'bgColour', defaultBgColour)
    const _qrStyle = setDefault(currentAccount, 'qrStyle', defaultQrStyle)
    const _qrTransparentLogo = showTransparency
      ? false
      : setDefault(currentAccount, 'transparentLogo', false) === 'true'
    setFgColour(_fgColour)
    setBgColour(_bgColour)
    setQrStyle(_qrStyle)
    setQrTransparentLogo(_qrTransparentLogo)
  }, [currentAccount?.qrSettings])

  const redrawCanvas = (useColour = '') => {
    if (canvas && canvas.current && imageFile !== '') {
      setLoaded(false)
      // @ts-ignore
      const context = canvas.current.getContext('2d')
      context.clearRect(0, 0, 200, 200)
      const image = new Image()
      if (!qrTransparentLogo) {
        context.fillStyle = useColour !== '' ? useColour : bgColour
        context.fillRect(0, 0, 200, 200)
      }
      image.onload = () => {
        const imageHeight = (160 * image.height) / image.width
        context.drawImage(image, 20, (200 - imageHeight) / 2, 160, imageHeight)
        setLoaded(true)
      }
      image.src = imageFile
    }
  }

  useEffect(() => {
    if (codes && selectedCodes.length > 0) {
      const cID = selectedCodes[0]
      const found = getItemByKeyValue(codes, 'cID', cID)
      if (found !== -1) {
        const { sL, c } = found
        const link = sL === '' ? c : `https://upl.inc/${sL}`
        setCode(link)
      }
    }
  }, [codes, selectedCodes])

  useEffect(() => {
    const useLogo = currentAccount?.qrCodeLogoList || []

    if (useLogo && useLogo.length > 0) {
      // eslint-disable-next-line
      ;(async function loadImage() {
        const res = await getQrLogoImage(useLogo[0])
        setImageFile(res?.data?.currentAccount?.qrCodeLogoSrc)
      })()
    }

    if (currentAccount && currentAccount.qrCodeLogoList.length === 0) {
      setImageFile(deafultLogoImage)
    }
  }, [currentAccount])

  // Simple hex colour validator
  const useFgColour = /^#([0-9A-F]{3}){1,2}$/i.test(fgColour)
    ? fgColour
    : defaultFgColour
  const useBgColour = /^#([0-9A-F]{3}){1,2}$/i.test(bgColour)
    ? bgColour
    : defaultBgColour

  const logoImage = useMemo(() => {
    if (showLogo && canvas && canvas.current && loaded) {
      // @ts-ignore
      return canvas.current.toDataURL('image/png', 1.0)
    }
    return ''
  }, [showLogo, canvas, loaded])

  useEffect(() => {
    redrawCanvas()
  }, [canvas, imageFile])

  const qrSize = useMemo(() => {
    const fileSize = getItemByKeyValue(sizes, 'value', selectedValue)
    if (fileSize !== -1) {
      return fileSize.qrSize
    }
    return sizes[0].qrSize
  }, [sizes, selectedValue])

  const logDownloadAndCopy = (action) => {
    let fileSize = getItemByKeyValue(sizes, 'value', selectedValue)
    if (action === 'copy') {
      fileSize = {
        label: 'Copy (400 x 400 px)',
      }
    }
    const longDescription = JSON.stringify({
      fgColor: useFgColour,
      bgColor: useBgColour,
      showLogo,
      fileType: selectedType,
      dimensions: fileSize !== -1 ? fileSize.label : '',
    })
    const actionData = {
      action: `qr-code-${action}-${section}`,
      longDescription,
      websiteSection: 'track',
    }
    dispatch(logActionAsync(actionData))
  }

  const getQrImage = () => {
    return (
      <>
        <div className={styles.hide}>
          <canvas ref={canvas} height={200} width={200} />
        </div>
        <div className={styles.qrCodeWrapper}>
          <div ref={previewRef}>
            <QRCode
              onClick={() => {
                setDefaultShowUploader(!defaultShowUploader)
              }}
              fgColor={useFgColour}
              bgColor={useBgColour}
              value={code}
              level="H"
              size={200}
              renderAs="canvas"
              imageSettings={
                showLogo
                  ? {
                      src: logoImage,
                      width: 60,
                      height: 60,
                      excavate: true,
                    }
                  : null
              }
            />
          </div>
          {!disableCopy && (
            <Button
              className={styles.copyButton}
              type="copy"
              align="left"
              onClick={async () => {
                if (previewRef && previewRef.current) {
                  // @ts-ignore
                  const c = previewRef.current.querySelector('canvas')
                  c.toBlob(
                    async (blob: Blob) => {
                      const data = new ClipboardItem({
                        'image/png': blob,
                      })
                      // @ts-ignore
                      await navigator.clipboard.write([data])
                    },
                    `image/${selectedType}`,
                    1.0,
                  )
                  logDownloadAndCopy('copy')
                }
              }}
            >
              Copy
            </Button>
          )}
          {children}
        </div>
      </>
    )
  }

  if (returnQrCodeOnly) {
    return <>{getQrImage()}</>
  }

  if (code.length > 1024) {
    return (
      <Modal
        narrow
        active
        toggleActive={() => {
          if (setShowModal) {
            setShowModal(false)
          }
        }}
        type="white"
      >
        <ModalHead>
          <Heading type={3} align="left">
            Download QR code
          </Heading>
        </ModalHead>
        <ModalBody>
          <p>
            Your url is longer than maximum characters we can support (max 1024
            characters). Please use a short link.
          </p>
        </ModalBody>
        <ModalFooter>
          <ConfirmationButtons
            type="simple"
            disabled
            hideDisabled
            onNo={(): void => {
              if (setShowModal) {
                setShowModal(false)
              }
            }}
            noLabel="Go back"
          />
        </ModalFooter>
      </Modal>
    )
  }

  return (
    <Modal
      narrow
      active
      toggleActive={() => {
        if (setShowModal) {
          setShowModal(false)
        }
      }}
      type="white"
    >
      <ModalHead>
        <Heading type={3} align="left">
          Download QR code
        </Heading>
      </ModalHead>
      <ModalBody>
        <TwoColumns className={styles.columns}>
          <Column main transparent equal className={styles.mainColumn}>
            <Label modalHeading id="qr">
              Preview
            </Label>
            <div className={styles.mainColumnInner}>
              {getQrImage()}
              <div className={styles.hide} key={nanoid()}>
                <div ref={ref}>
                  <QRCode
                    fgColor={useFgColour}
                    bgColor={useBgColour}
                    value={code}
                    level="H"
                    size={
                      selectedType === 'svg'
                        ? 600
                        : window.parseInt(selectedValue, 10)
                    }
                    renderAs={selectedType === 'svg' ? 'svg' : 'canvas'}
                    imageSettings={
                      showLogo
                        ? {
                            src: logoImage,
                            width: selectedType === 'svg' ? 180 : qrSize,
                            height: selectedType === 'svg' ? 180 : qrSize,
                            excavate: true,
                          }
                        : null
                    }
                  />
                </div>
              </div>
            </div>
            <div className={classNames(styles.checkboxRow, styles.inlineTitle)}>
              <Input
                prefix="Logo"
                type="checkbox"
                id="showLogo"
                name="showLogo"
                checked={showLogo}
                label=" "
                onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                  const { checked } = e.target as HTMLInputElement
                  setShowLogo(checked)
                  const actionData = {
                    action: checked ? `include-qr-logo` : `exclude-qr-logo`,
                    longDescription: '',
                    websiteSection: 'track',
                  }
                  dispatch(logActionAsync(actionData))
                }}
              />
              {showLogo &&
                currentAccount &&
                Array.isArray(currentAccount.qrCodeLogoList) &&
                currentAccount.qrCodeLogoList.map((logoLinkQR) => {
                  return (
                    <ImgUploader
                      defaultShowUploader={defaultShowUploader}
                      onSetShowUploader={(val) => {
                        setDefaultShowUploader(val)
                      }}
                      uploadButtonText="Upload logo"
                      compact
                      key={`${logoLinkQR}`}
                      logoLink={logoLinkQR}
                      imgType="qrLogo"
                      uploadFn={async (file: File) => {
                        await deleteQrLogo(logoLinkQR)
                        const res = await uploadQrLogo({ file })
                        if (res === true) {
                          dispatch(getUserAccountDetailsGQL())
                          const actionData = {
                            action: 'company-qr-logo-changed',
                            longDescription: '',
                            websiteSection: 'settings',
                          }
                          dispatch(logActionAsync(actionData))
                          return true
                        }
                        return false
                      }}
                      deleteFn={
                        currentAccount.qrCodeLogoList.length > 1
                          ? async () => {
                              await deleteQrLogo(logoLinkQR)
                              dispatch(getUserAccountDetailsGQL())
                              const actionData = {
                                action: 'company-qr-logo-deleted',
                                longDescription: '',
                                websiteSection: 'report',
                              }
                              dispatch(logActionAsync(actionData))
                            }
                          : undefined
                      }
                    />
                  )
                })}
              {showLogo &&
                currentAccount &&
                Array.isArray(currentAccount.qrCodeLogoList) &&
                currentAccount.qrCodeLogoList.length === 0 && (
                  <ImgUploader
                    defaultShowUploader={false}
                    uploadButtonText="Upload logo"
                    onSetShowUploader={(val) => {
                      setDefaultShowUploader(val)
                    }}
                    compact
                    logoLink=""
                    imgType="qrLogo"
                    uploadFn={async (file: File) => {
                      const res = await uploadQrLogo({ file })
                      if (res === true) {
                        dispatch(getUserAccountDetailsGQL())
                        const actionData = {
                          action: 'company-qr-logo-changed',
                          longDescription: '',
                          websiteSection: 'settings',
                        }
                        dispatch(logActionAsync(actionData))
                        return true
                      }
                      return false
                    }}
                  />
                )}
            </div>
            {showShape && (
              <div className={styles.row}>
                <Label modalHeading id="qrType">
                  Shape
                </Label>
                <div className={styles.alignRow}>
                  <Input
                    name="qrType"
                    id={nanoid()}
                    label="Squares"
                    type="radio"
                    value="squares"
                    checked={qrStyle !== 'dots'}
                    onClick={(e): any => {
                      e.preventDefault()
                      setQrStyle('squares')
                      const actionData = {
                        action: `update-qr-style`,
                        longDescription: 'squares',
                        websiteSection: 'track',
                      }
                      dispatch(logActionAsync(actionData))
                    }}
                  />
                  <Input
                    name="qrType"
                    id={nanoid()}
                    label="Dots"
                    type="radio"
                    value="dots"
                    checked={qrStyle === 'dots'}
                    onClick={(e): any => {
                      e.preventDefault()
                      setQrStyle('dots')
                      const actionData = {
                        action: `update-qr-style`,
                        longDescription: 'dots',
                        websiteSection: 'track',
                      }
                      dispatch(logActionAsync(actionData))
                    }}
                  />
                </div>
              </div>
            )}
            <Label modalHeading id="code">
              Shape colour
            </Label>
            <ColourPicker
              defaultColour={setDefault(
                currentAccount,
                'fgColour',
                defaultFgColour,
              )}
              setColor={(c) => {
                setFgColour(c)
                const actionData = {
                  action: 'update-qr-fg-colour',
                  longDescription: c,
                  websiteSection: 'track',
                }
                dispatch(logActionAsync(actionData))
              }}
              color={fgColour}
            />
            <Label modalHeading id="code">
              Background colour
            </Label>
            <ColourPicker
              defaultColour={setDefault(
                currentAccount,
                'bgColour',
                defaultBgColour,
              )}
              setColor={(c) => {
                setBgColour(c)
                redrawCanvas(c)
                const actionData = {
                  action: 'update-qr-bg-colour',
                  longDescription: c,
                  websiteSection: 'track',
                }
                dispatch(logActionAsync(actionData))
              }}
              color={bgColour}
            />
          </Column>
          <Column side transparent equal className={styles.sideColumn}>
            <OuterBox panel>
              <InnerBox className={styles.topBox}>
                <div className={styles.row}>
                  <Label modalHeading id="code">
                    Destination
                  </Label>
                  {code.length <= 140 && (
                    <p className={styles.passiveInput}>{code}</p>
                  )}
                  {code.length > 140 && (
                    <Input
                      className={styles.passiveInput}
                      readOnly
                      name="code"
                      id="code"
                      value={code}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>,
                      ) => {
                        const { value: val } = event.target
                        setCode(val)
                      }}
                    />
                  )}
                </div>
                <div className={styles.row}>
                  <Label modalHeading id="type">
                    File type
                  </Label>
                  <div className={styles.alignRow}>
                    <Input
                      name="type"
                      id={nanoid()}
                      label="PNG"
                      type="radio"
                      value="png"
                      checked={selectedType === 'png'}
                      onClick={(e): any => {
                        e.preventDefault()
                        setSelectedType('png')
                        const actionData = {
                          action: `update-qr-filetype-png`,
                          longDescription: '',
                          websiteSection: 'track',
                        }
                        dispatch(logActionAsync(actionData))
                      }}
                    />
                    <Input
                      name="type"
                      id={nanoid()}
                      label="JPG"
                      type="radio"
                      value="jpg"
                      checked={selectedType === 'jpg'}
                      onClick={(e): any => {
                        e.preventDefault()
                        setSelectedType('jpg')
                        const actionData = {
                          action: `update-qr-filetype-jpg`,
                          longDescription: '',
                          websiteSection: 'track',
                        }
                        dispatch(logActionAsync(actionData))
                      }}
                    />
                    <Input
                      name="type"
                      id={nanoid()}
                      label="SVG"
                      type="radio"
                      value="png"
                      checked={selectedType === 'svg'}
                      onClick={(e): any => {
                        e.preventDefault()
                        setSelectedType('svg')
                        const actionData = {
                          action: `update-qr-filetype-svg`,
                          longDescription: '',
                          websiteSection: 'track',
                        }
                        dispatch(logActionAsync(actionData))
                      }}
                    />
                  </div>
                </div>

                {selectedType !== 'svg' && (
                  <div className={styles.row}>
                    <Label modalHeading id="size">
                      Image dimensions
                    </Label>
                    {sizes.map((size) => (
                      <Input
                        name="size"
                        id={nanoid()}
                        key={nanoid()}
                        label={size.label}
                        type="radio"
                        value={size.value}
                        checked={selectedValue === size.value}
                        onClick={(): any => {
                          setSelectedValue(size.value)
                          const fileSize = getItemByKeyValue(
                            sizes,
                            'value',
                            size.value,
                          )
                          const actionData = {
                            action: `update-qr-dimension-${
                              fileSize !== -1 ? fileSize.size : ''
                            }`,
                            longDescription:
                              fileSize !== -1 ? fileSize.label : '',
                            websiteSection: 'track',
                          }
                          dispatch(logActionAsync(actionData))
                        }}
                      />
                    ))}
                  </div>
                )}
              </InnerBox>
            </OuterBox>
          </Column>
        </TwoColumns>
      </ModalBody>
      <ModalFooter>
        <ConfirmationButtons
          disabled={code === ''}
          type="simple"
          onYes={async () => {
            if (ref && ref.current) {
              if (selectedType === 'svg') {
                // @ts-ignore
                const s = ref.current.querySelector('svg')
                // eslint-disable-next-line
                // debugger
                const content = s.innerHTML
                const viewBox = s.getAttribute('viewBox')
                const height = s.getAttribute('height')
                const width = s.getAttribute('width')
                const start = `<svg shape-rendering="crispEdges" height="${height}" width="${width}" viewBox="${viewBox}" xml:space="preserve" xmlns:xlink="http://www.w3.org/1999/xlink" xmlns="http://www.w3.org/2000/svg">`
                const blob = new Blob([`${start}${content}</svg>`], {
                  type: 'image/svg+xml',
                })
                await downloadQRCode(blob, selectedType, code)
              } else {
                // @ts-ignore
                const c = ref.current.querySelector('canvas')
                c.toBlob(
                  async (blob) => {
                    await downloadQRCode(blob, selectedType, code)
                  },
                  `image/${selectedType}`,
                  1.0,
                )
              }
              logDownloadAndCopy('download')
            }
          }}
          onNo={(): void => {
            if (setShowModal) {
              setShowModal(false)
            }
          }}
          yesLabel="Download QR code"
          noLabel="Go back"
        >
          <p className={styles.saveNote}>
            Always test your <strong>QR code</strong> before use!
          </p>
        </ConfirmationButtons>
      </ModalFooter>
    </Modal>
  )
}
